import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';

// Web Speech API types
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
}

interface AgentChatProps {
  projectId: number;
  projectName: string;
  onWorkingStateChange?: (working: boolean) => void;
}

interface Conversation {
  id: number;
  title: string;
  status: string;
  total_tokens_used: number;
  created_at: string;
  updated_at: string;
  active_task?: { task_id: number; task_status: string } | null;
}

interface Message {
  id?: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  tool_calls?: any[];
  tool_results?: any[];
  tokens_used?: number;
  created_at?: string;
  isStreaming?: boolean;
}

interface ToolActivity {
  name: string;
  input: any;
  result?: any;
  detail?: string;
  tool_use_id?: string;
  success?: boolean;
}

interface AgentTask {
  id: number;
  conversation_id: number;
  status: string;
  user_message: string;
  current_turn: number;
  total_input_tokens: number;
  total_output_tokens: number;
  error_message?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

const AgentChat: React.FC<AgentChatProps> = ({ projectId, projectName, onWorkingStateChange }) => {
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [toolActivities, setToolActivities] = useState<ToolActivity[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeTask, setActiveTask] = useState<AgentTask | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Notify parent when Ada's working state changes
  useEffect(() => {
    const isWorking = activeTask !== null && !['completed', 'failed', 'cancelled'].includes(activeTask.status);
    onWorkingStateChange?.(isWorking);
  }, [activeTask, onWorkingStateChange]);

  // Voice input (speech-to-text)
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const SpeechRecognitionAPI = typeof window !== 'undefined'
    ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    : null;
  const speechInputSupported = !!SpeechRecognitionAPI;

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  const startListening = useCallback(() => {
    if (!SpeechRecognitionAPI) return;
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }

    const recognition: SpeechRecognitionInstance = new SpeechRecognitionAPI();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-GB';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setInputValue(transcript);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error !== 'aborted') {
        console.error('Speech recognition error:', event.error);
        setErrorMessage(event.error === 'not-allowed'
          ? 'Microphone access denied. Please allow mic access in your browser.'
          : 'Voice recognition failed. Please try again.');
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    setIsListening(true);
    setErrorMessage('');
    recognition.start();
  }, [SpeechRecognitionAPI]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, stopListening, startListening]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) recognitionRef.current.abort();
    };
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load conversations for this project and check for active tasks
  useEffect(() => {
    if (!token || !projectId) return;
    loadConversations();
    checkActiveTasks();
  }, [token, projectId]);

  // Cleanup SSE on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  const loadConversations = async () => {
    try {
      const res = await fetch(`/api/agent/conversations?project_id=${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 403) {
        setAccessDenied(true);
        return;
      }
      if (!res.ok) return;
      const data = await res.json();
      setConversations(data.conversations);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    }
  };

  const checkActiveTasks = async () => {
    try {
      const res = await fetch(`/api/agent/tasks?project_id=${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const tasks: AgentTask[] = await res.json();
      const runningTask = tasks.find(t => t.status === 'running' || t.status === 'queued');
      if (runningTask) {
        setActiveTask(runningTask);
        setIsLoading(true);
        // Load the conversation this task belongs to
        const convRes = await fetch(`/api/agent/conversations/${runningTask.conversation_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (convRes.ok) {
          const data = await convRes.json();
          setActiveConversation(data.conversation);
          setMessages(data.messages);
        }
        // Reconnect to the task's SSE stream
        followTask(runningTask.id);
      }
    } catch (err) {
      // Tasks endpoint may not exist yet
    }
  };

  const followTask = async (taskId: number) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const res = await fetch(`/api/agent/tasks/${taskId}/follow`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      });
      if (!res.ok) return;

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let assistantText = '';
      let buffer = '';
      let isReplaying = false;

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            // During replay, accumulate text silently
            if (event.type === 'replay_start') { isReplaying = true; continue; }
            if (event.type === 'replay_end') {
              isReplaying = false;
              // Update the assistant message with accumulated text
              if (assistantText) {
                setMessages(prev => {
                  const updated = [...prev];
                  const lastIdx = updated.length - 1;
                  if (updated[lastIdx]?.role === 'assistant') {
                    updated[lastIdx] = { ...updated[lastIdx], content: assistantText, isStreaming: true };
                  }
                  return updated;
                });
              }
              continue;
            }
            if (event.type === 'task_status') {
              if (event.status === 'completed' || event.status === 'failed' || event.status === 'cancelled') {
                setIsLoading(false);
                setActiveTask(null);
                setStatusMessage('');
                // Reload conversations list
                loadConversations();
              }
              continue;
            }

            // Process events same as chat stream
            handleSSEEvent(event, assistantText, (text) => { assistantText = text; }, isReplaying);
          } catch (e) { /* skip malformed */ }
        }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('Follow task error:', err);
      }
    }
  };

  const createConversation = async (): Promise<Conversation | null> => {
    try {
      const res = await fetch('/api/agent/conversations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ project_id: projectId })
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        console.error('Failed to create conversation:', res.status, errorData);
        setErrorMessage(`Failed to start conversation: ${errorData.detail || errorData.error || res.statusText}`);
        return null;
      }
      const data = await res.json();
      setConversations(prev => [data.conversation, ...prev]);
      setActiveConversation(data.conversation);
      setMessages([]);
      setToolActivities([]);
      setErrorMessage('');
      return data.conversation;
    } catch (err) {
      console.error('Failed to create conversation:', err);
      setErrorMessage('Failed to connect to Ada. Please try again.');
      return null;
    }
  };

  const loadConversation = async (conv: Conversation) => {
    try {
      const res = await fetch(`/api/agent/conversations/${conv.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setActiveConversation(data.conversation);
      setMessages(data.messages);
      setToolActivities([]);
      setShowHistory(false);

      // Check if this conversation has an active task
      try {
        const tasksRes = await fetch(`/api/agent/tasks?conversation_id=${conv.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (tasksRes.ok) {
          const tasks: AgentTask[] = await tasksRes.json();
          const runningTask = tasks.find(t => t.status === 'running' || t.status === 'queued');
          const failedTask = tasks.find(t => t.status === 'failed' || t.status === 'paused');
          if (runningTask) {
            setActiveTask(runningTask);
            setIsLoading(true);
            followTask(runningTask.id);
          } else if (failedTask) {
            setActiveTask(failedTask);
          }
        }
      } catch (e) { /* tasks endpoint may not exist */ }

      inputRef.current?.focus();
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  };

  const startNewConversation = () => {
    setActiveConversation(null);
    setMessages([]);
    setToolActivities([]);
    setShowHistory(false);
    inputRef.current?.focus();
  };

  // Shared SSE event handler
  const handleSSEEvent = (event: any, currentText: string, setText: (t: string) => void, silent = false) => {
    switch (event.type) {
      case 'text':
        const newText = currentText + event.content;
        setText(newText);
        if (!silent) {
          setMessages(prev => {
            const updated = [...prev];
            const lastIdx = updated.length - 1;
            if (updated[lastIdx]?.role === 'assistant') {
              updated[lastIdx] = { ...updated[lastIdx], content: newText, isStreaming: true };
            }
            return updated;
          });
        }
        break;

      case 'status':
        setStatusMessage(event.message);
        break;

      case 'task_created':
        setActiveTask({ id: event.task_id } as AgentTask);
        break;

      case 'tool_start':
        setToolActivities(prev => [...prev, {
          name: event.tool,
          input: event.input,
          tool_use_id: event.tool_use_id,
          detail: event.detail
        }]);
        setStatusMessage(event.detail || `Using ${formatToolName(event.tool)}...`);
        break;

      case 'tool_use':
        setToolActivities(prev => [...prev, { name: event.name, input: event.input }]);
        setStatusMessage(`Using ${formatToolName(event.name)}...`);
        break;

      case 'tool_complete':
        setToolActivities(prev =>
          prev.map(ta =>
            (ta.tool_use_id === event.tool_use_id || (ta.name === event.tool && !ta.result))
              ? { ...ta, result: true, detail: event.detail, success: true }
              : ta
          )
        );
        setStatusMessage(event.detail || '');
        break;

      case 'tool_error':
        setToolActivities(prev =>
          prev.map(ta =>
            (ta.tool_use_id === event.tool_use_id || (ta.name === event.tool && !ta.result))
              ? { ...ta, result: true, detail: event.detail, success: false }
              : ta
          )
        );
        break;

      case 'tool_result':
        setToolActivities(prev =>
          prev.map(ta =>
            ta.name === event.name && !ta.result
              ? { ...ta, result: event.result }
              : ta
          )
        );
        break;

      case 'title_update':
        if (event.title) {
          setActiveConversation(prev => prev ? { ...prev, title: event.title } : null);
          setConversations(prev =>
            prev.map(c => c.id === activeConversation?.id ? { ...c, title: event.title } : c)
          );
        }
        break;

      case 'done':
        setMessages(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (updated[lastIdx]?.role === 'assistant') {
            updated[lastIdx] = { ...updated[lastIdx], isStreaming: false };
          }
          return updated;
        });
        setIsLoading(false);
        setActiveTask(null);
        setStatusMessage('');
        break;

      case 'error':
        setMessages(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (updated[lastIdx]?.role === 'assistant') {
            updated[lastIdx] = {
              ...updated[lastIdx],
              content: `Error: ${event.message}`,
              isStreaming: false
            };
          }
          return updated;
        });
        setIsLoading(false);
        setActiveTask(null);
        setStatusMessage('');
        break;

      case 'cancelled':
        setIsLoading(false);
        setActiveTask(null);
        setStatusMessage('Task cancelled');
        break;
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setIsLoading(true);
    setStatusMessage('');
    setToolActivities([]);

    // Auto-create conversation if none active
    let conv = activeConversation;
    if (!conv) {
      conv = await createConversation();
      if (!conv) {
        setIsLoading(false);
        return;
      }
    }

    // Add user message immediately
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setMessages(prev => [...prev, { role: 'assistant', content: '', isStreaming: true }]);

    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const res = await fetch('/api/agent/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          conversation_id: conv.id,
          message: userMessage
        }),
        signal: controller.signal
      });

      if (!res.ok) throw new Error('Failed to send message');

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let assistantText = '';
      let buffer = '';

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            handleSSEEvent(event, assistantText, (text) => { assistantText = text; });
          } catch (e) {
            // Skip malformed SSE lines
          }
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        // User navigated away or refreshed - task continues in background
        return;
      }
      console.error('Chat error:', err);
      setMessages(prev => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (updated[lastIdx]?.role === 'assistant') {
          updated[lastIdx] = {
            ...updated[lastIdx],
            content: 'Connection lost. The task is still running in the background. Refresh to reconnect.',
            isStreaming: false
          };
        }
        return updated;
      });
    } finally {
      if (!activeTask) {
        setIsLoading(false);
        setStatusMessage('');
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatToolName = (name: string) => {
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (accessDenied) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center max-w-md">
          <img src="https://i.ibb.co/KjJ85nc3/ada-AI-Agent.png" alt="Ada" className="w-16 h-16 mx-auto mb-4 rounded-2xl object-cover" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Ada - Coming Soon</h2>
          <p className="text-gray-500">Ada is currently in private beta. She will be available to all users soon.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex-1 min-w-0">
          <h2 className="text-xs font-semibold text-gray-900 truncate">
            {activeConversation ? activeConversation.title : `Research assistant`}
          </h2>
          {activeConversation && (
            <p className="text-[10px] text-gray-400">
              {activeConversation.total_tokens_used?.toLocaleString() || 0} tokens
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {activeConversation && (
            <button
              onClick={startNewConversation}
              className="text-xs text-gray-500 hover:text-blue-600 transition-colors px-2 py-1 rounded hover:bg-gray-50"
            >
              + New
            </button>
          )}
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`text-xs px-2 py-1 rounded transition-colors ${
              showHistory ? 'text-blue-600 bg-blue-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            History ({conversations.length})
          </button>
        </div>
      </div>

      {/* History dropdown */}
      {showHistory && conversations.length > 0 && (
        <div className="border-b border-gray-100 bg-gray-50 px-6 py-2 max-h-48 overflow-y-auto flex-shrink-0">
          {conversations.map(conv => (
            <div
              key={conv.id}
              className={`flex items-center gap-1 rounded-lg text-sm hover:bg-white transition-colors ${
                activeConversation?.id === conv.id ? 'bg-white shadow-sm' : ''
              }`}
            >
              {/* Active task indicator */}
              {conv.active_task ? (
                <span className="w-2 h-2 ml-2 flex-shrink-0 rounded-full bg-green-500 animate-pulse" title="Ada is working" />
              ) : (
                <span className="w-2 h-2 ml-2 flex-shrink-0" />
              )}
              <button
                onClick={() => loadConversation(conv)}
                className="flex-1 text-left px-2 py-2 truncate flex items-center justify-between min-w-0"
              >
                <span className="text-gray-700 truncate">{conv.title}</span>
                <span className="text-xs text-gray-400 ml-2 flex-shrink-0">{formatTime(conv.updated_at || conv.created_at)}</span>
              </button>
              {/* Delete button */}
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!window.confirm('Delete this conversation?')) return;
                  try {
                    const res = await fetch(`/api/agent/conversations/${conv.id}`, {
                      method: 'DELETE',
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (res.ok) {
                      setConversations(prev => prev.filter(c => c.id !== conv.id));
                      if (activeConversation?.id === conv.id) {
                        setActiveConversation(null);
                        setMessages([]);
                        setToolActivities([]);
                      }
                    }
                  } catch (err) { console.error('Failed to delete conversation:', err); }
                }}
                className="p-1.5 mr-1 text-gray-300 hover:text-red-500 transition-colors flex-shrink-0 rounded hover:bg-red-50"
                title="Delete conversation"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center px-2">
              <p className="text-gray-400 mb-3 text-sm">How can I help with {projectName}?</p>
              <div className="flex flex-wrap gap-1.5 justify-center">
                {[
                  'Show me project stats',
                  'Search for Python skills',
                  'List all companies',
                  'Find candidates in London',
                  'Most senior candidates?',
                ].map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setInputValue(suggestion);
                      inputRef.current?.focus();
                    }}
                    className="px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-full text-xs text-gray-600 hover:bg-white hover:border-gray-300 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[90%] rounded-2xl px-3 py-2 ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-50 border border-gray-200 text-gray-800'
                  }`}
                >
                  {msg.role === 'assistant' && msg.isStreaming && !msg.content ? (
                    <div className="min-w-[200px]">
                      {toolActivities.length > 0 ? (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 mb-2">
                            <img src="https://i.ibb.co/KjJ85nc3/ada-AI-Agent.png" alt="Ada" className="w-5 h-5 rounded-full" />
                            <span className="text-xs font-medium text-gray-500">Ada is working...</span>
                          </div>
                          {toolActivities.map((ta, i) => {
                            const isDone = !!ta.result;
                            const isFailed = ta.success === false;
                            return (
                              <div
                                key={i}
                                className={`flex items-center gap-2 text-xs ${
                                  isFailed ? 'text-red-600' : isDone ? 'text-gray-400' : 'text-blue-700'
                                }`}
                              >
                                {isFailed ? (
                                  <svg className="w-3.5 h-3.5 flex-shrink-0 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                ) : isDone ? (
                                  <svg className="w-3.5 h-3.5 flex-shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <svg className="w-3.5 h-3.5 flex-shrink-0 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                  </svg>
                                )}
                                <span className="truncate">{ta.detail || formatToolName(ta.name)}</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-gray-400">
                          <img src="https://i.ibb.co/KjJ85nc3/ada-AI-Agent.png" alt="Ada" className="w-5 h-5 rounded-full" />
                          <span className="text-xs">Thinking...</span>
                          <div className="flex gap-1">
                            <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm whitespace-pre-wrap leading-relaxed">
                      {formatMessageContent(msg.content)}
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Status bar */}
      {(statusMessage || activeTask) && (
        <div className="px-3 py-1.5 bg-blue-50 border-t border-blue-100 flex-shrink-0 flex items-center justify-between">
          <p className="text-[11px] text-blue-600 truncate flex-1">
            {statusMessage || 'Ada is working in the background...'}
          </p>
          {activeTask && (
            <button
              onClick={async () => {
                try {
                  await fetch(`/api/agent/tasks/${activeTask.id}/cancel`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  setIsLoading(false);
                  setActiveTask(null);
                  setStatusMessage('Task cancelled');
                  if (abortControllerRef.current) abortControllerRef.current.abort();
                } catch (e) { /* ignore */ }
              }}
              className="text-[11px] text-red-500 hover:text-red-700 ml-2 flex-shrink-0 font-medium"
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {/* Failed/paused task banner */}
      {activeTask && (activeTask.status === 'failed' || activeTask.status === 'paused') && (
        <div className="px-3 py-2 bg-amber-50 border-t border-amber-200 flex-shrink-0 flex items-center justify-between">
          <p className="text-xs text-amber-700">
            {activeTask.status === 'failed' ? 'Task failed' : 'Task paused'}
            {activeTask.error_message ? `: ${activeTask.error_message}` : ''}
          </p>
          <button
            onClick={async () => {
              setIsLoading(true);
              setToolActivities([]);
              setMessages(prev => [...prev, { role: 'assistant', content: '', isStreaming: true }]);

              if (abortControllerRef.current) abortControllerRef.current.abort();
              const controller = new AbortController();
              abortControllerRef.current = controller;

              try {
                const res = await fetch(`/api/agent/tasks/${activeTask.id}/resume`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                  signal: controller.signal
                });
                if (!res.ok) throw new Error('Failed to resume');

                const reader = res.body?.getReader();
                const decoder = new TextDecoder();
                let assistantText = '';
                let buffer = '';

                while (reader) {
                  const { done, value } = await reader.read();
                  if (done) break;
                  buffer += decoder.decode(value, { stream: true });
                  const lines = buffer.split('\n');
                  buffer = lines.pop() || '';
                  for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                      const event = JSON.parse(line.slice(6));
                      handleSSEEvent(event, assistantText, (text) => { assistantText = text; });
                    } catch (e) { /* skip */ }
                  }
                }
              } catch (err: any) {
                if (err.name !== 'AbortError') {
                  setErrorMessage('Failed to resume task');
                }
              } finally {
                setIsLoading(false);
              }
            }}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium ml-2 flex-shrink-0"
          >
            Resume
          </button>
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-gray-200 px-3 py-3 flex-shrink-0">
        <div className="flex items-end gap-2">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isListening ? 'Listening... speak to Ada' : 'Ask about your project data...'}
              className={`w-full px-3 py-2 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm leading-relaxed ${
                isListening ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              rows={1}
              style={{ minHeight: '40px', maxHeight: '100px' }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, 100) + 'px';
              }}
              disabled={isLoading}
            />
          </div>
          {speechInputSupported && (
            <button
              onClick={toggleListening}
              disabled={isLoading}
              title={isListening ? 'Stop listening' : 'Speak to Ada'}
              className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                isListening
                  ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
              </svg>
            </button>
          )}
          <button
            onClick={sendMessage}
            disabled={!inputValue.trim() || isLoading}
            className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
            </svg>
          </button>
        </div>
        {errorMessage && (
          <p className="text-[11px] text-red-500 mt-1.5 text-center">{errorMessage}</p>
        )}
      </div>
    </div>
  );
};

function formatMessageContent(content: string): React.ReactNode {
  if (!content) return null;

  const parts = content.split(/(```[\s\S]*?```)/g);

  return parts.map((part, i) => {
    if (part.startsWith('```') && part.endsWith('```')) {
      const code = part.slice(3, -3).replace(/^\w+\n/, '');
      return (
        <pre key={i} className="bg-gray-100 rounded-lg p-3 my-2 overflow-x-auto text-xs font-mono">
          {code}
        </pre>
      );
    }

    return (
      <span key={i}>
        {part.split('\n').map((line, j) => {
          if (line.startsWith('### ')) {
            return <div key={j} className="font-semibold text-base mt-3 mb-1">{line.slice(4)}</div>;
          }
          if (line.startsWith('## ')) {
            return <div key={j} className="font-bold text-base mt-3 mb-1">{line.slice(3)}</div>;
          }
          let formatted: React.ReactNode = line;
          if (line.includes('**')) {
            const boldParts = line.split(/\*\*(.*?)\*\*/g);
            formatted = boldParts.map((bp, k) =>
              k % 2 === 1 ? <strong key={k}>{bp}</strong> : bp
            );
          }
          if (line.startsWith('- ') || line.startsWith('* ')) {
            return <div key={j} className="ml-4 flex gap-1"><span className="text-gray-400">-</span> <span>{typeof formatted === 'string' ? formatted.slice(2) : formatted}</span></div>;
          }
          if (/^\d+\.\s/.test(line)) {
            const num = line.match(/^(\d+)\./)?.[1];
            return <div key={j} className="ml-4 flex gap-1"><span className="text-gray-400 w-4">{num}.</span> <span>{typeof formatted === 'string' ? formatted.replace(/^\d+\.\s/, '') : formatted}</span></div>;
          }
          if (line === '') return <br key={j} />;
          return <div key={j}>{formatted}</div>;
        })}
      </span>
    );
  });
}

export default AgentChat;
